<template>
  <transition name="cookie-slide">
    <div
      v-if="consent.bannerVisible"
      class="cookie-banner"
      role="dialog"
      aria-modal="false"
      :aria-label="$t('cookieConsent.title')"
    >
      <div class="cookie-banner-inner">
        <div class="cookie-text">
          <p class="cookie-title">{{ $t('cookieConsent.title') }}</p>
          <p class="cookie-description">
            {{ $t('cookieConsent.description') }}
            <router-link to="/cookiepolicy" class="cookie-policy-link">
              {{ $t('cookieConsent.policyLink') }}
            </router-link>
          </p>
        </div>

        <div v-if="consent.preferencesOpen" class="cookie-preferences">
          <div class="cookie-category">
            <div class="cookie-category-text">
              <p class="cookie-category-title">{{ $t('cookieConsent.necessary.title') }}</p>
              <p class="cookie-category-description">{{ $t('cookieConsent.necessary.description') }}</p>
            </div>
            <label class="cookie-switch is-disabled">
              <input type="checkbox" checked disabled />
              <span class="cookie-slider"></span>
            </label>
          </div>

          <div class="cookie-category">
            <div class="cookie-category-text">
              <p class="cookie-category-title">{{ $t('cookieConsent.externalMedia.title') }}</p>
              <p class="cookie-category-description">{{ $t('cookieConsent.externalMedia.description') }}</p>
            </div>
            <label class="cookie-switch">
              <input type="checkbox" v-model="externalMediaChoice" />
              <span class="cookie-slider"></span>
            </label>
          </div>
        </div>

        <div class="cookie-actions">
          <button
            v-if="!consent.preferencesOpen"
            type="button"
            class="cookie-btn cookie-btn-ghost"
            @click="showPreferences"
          >
            {{ $t('cookieConsent.customize') }}
          </button>
          <button
            v-else
            type="button"
            class="cookie-btn cookie-btn-ghost"
            @click="saveChoice"
          >
            {{ $t('cookieConsent.save') }}
          </button>
          <button type="button" class="cookie-btn cookie-btn-secondary" @click="rejectNonEssential">
            {{ $t('cookieConsent.rejectAll') }}
          </button>
          <button type="button" class="cookie-btn cookie-btn-primary" @click="acceptAll">
            {{ $t('cookieConsent.acceptAll') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch } from 'vue';
import { useCookieConsent } from '../../composables/useCookieConsent.js';

export default {
  name: 'CookieBanner',
  setup() {
    const { consent, acceptAll, rejectNonEssential, savePreferences, openPreferences } = useCookieConsent();
    const externalMediaChoice = ref(consent.externalMedia);

    // Keep the toggle in sync when preferences are reopened later.
    watch(
      () => consent.bannerVisible,
      (visible) => {
        if (visible) externalMediaChoice.value = consent.externalMedia;
      }
    );

    return {
      consent,
      externalMediaChoice,
      acceptAll,
      rejectNonEssential,
      showPreferences: openPreferences,
      saveChoice: () => savePreferences(externalMediaChoice.value),
    };
  },
};
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  background-color: #1c1c1c;
  color: rgba(255, 255, 255, 0.85);
  box-shadow: 0 -6px 25px rgba(0, 0, 0, 0.35);
  padding: 22px 20px;
  box-sizing: border-box;
}

.cookie-banner-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.cookie-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 6px 0;
}

.cookie-description {
  font-size: 0.9rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.75);
  margin: 0;
}

.cookie-policy-link {
  color: #bbd55a;
  text-decoration: underline;
  font-weight: 500;
  transition: color 0.3s ease;
}

.cookie-policy-link:hover {
  color: #a0c24a;
}

/* --- Preferences panel --- */
.cookie-preferences {
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  padding: 14px 0;
}

.cookie-category {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.cookie-category-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 4px 0;
}

.cookie-category-description {
  font-size: 0.85rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
}

/* --- Toggle switch --- */
.cookie-switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 26px;
  flex-shrink: 0;
}

.cookie-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.cookie-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.25);
  border-radius: 26px;
  transition: background-color 0.3s ease;
}

.cookie-slider::before {
  content: '';
  position: absolute;
  height: 20px;
  width: 20px;
  left: 3px;
  top: 3px;
  background-color: #ffffff;
  border-radius: 50%;
  transition: transform 0.3s ease;
}

.cookie-switch input:checked + .cookie-slider {
  background-color: #bbd55a;
}

.cookie-switch input:checked + .cookie-slider::before {
  transform: translateX(20px);
}

.cookie-switch.is-disabled .cookie-slider {
  cursor: not-allowed;
  opacity: 0.6;
}

/* --- Actions --- */
.cookie-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.cookie-btn {
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 10px 22px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.cookie-btn-primary {
  background-color: #bbd55a;
  color: #1c1c1c;
}

.cookie-btn-primary:hover {
  background-color: #a0c24a;
}

.cookie-btn-secondary {
  background-color: transparent;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.cookie-btn-secondary:hover {
  border-color: #ffffff;
  background-color: rgba(255, 255, 255, 0.08);
}

.cookie-btn-ghost {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: underline;
}

.cookie-btn-ghost:hover {
  color: #bbd55a;
}

/* --- Transition --- */
.cookie-slide-enter-active,
.cookie-slide-leave-active {
  transition: transform 0.4s ease, opacity 0.4s ease;
}

.cookie-slide-enter-from,
.cookie-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* --- Responsive --- */
@media (max-width: 768px) {
  .cookie-banner {
    padding: 18px 15px;
  }

  .cookie-category {
    align-items: flex-start;
  }

  .cookie-actions {
    justify-content: stretch;
    flex-direction: column-reverse;
  }

  .cookie-btn {
    width: 100%;
    padding: 12px;
  }
}
</style>
